//================================================================
//  Tab: Overview
//================================================================

//  Purpose: This tab displays all 'Overview' of a Subnets

//  Example:
//    <Overview></Overview>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser, GetRoles } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import LiveField from '../../../../../Components/LiveField/LiveField';
import DeleteSubnetModal from './Components/DeleteSubnetModal';

//Functions
import QueryListener from '../../../../../Library/QueryListener';
import CheckInventoryRole from '../../../../../Library/CheckInventoryRole';

//Images
import DeleteIcon from '../../../../../Components/Images/Icon_Bin_Red.svg';


export default function Overview() {

    //------------------------------------------------------
    //  useContext & React Router
    //------------------------------------------------------

    const params = useParams();

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');
    const [roleStatus, setRoleStatus] = useState('download'); // download, write & admin

    // Open and close the delete pane (using the current Firestore document)
    const [openDeletePane, setOpenDeletePane] = useState(undefined);

    // Holds the application document from 
    const [subnetDoc, setSubnetDoc] = useState();

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Query Listener
    // - Get Firestore document
    useEffect(() => {

        const subnetid = params.id;

        if (subnetid === '') return;
        if (subnetid === null) return;
        if (getRoles === undefined) return;
        if (getUser === undefined) return;

        // ============================================
        //  Perform role check
        // ============================================

        const roleStatus = CheckInventoryRole('subnets', getRoles, getUser);
        if (roleStatus === 'access-denied') return setPageStatus('access-denied');
        setRoleStatus(roleStatus);

        // ============================================
        //  Fetch Data
        // ============================================

        function onLoadChange(documents) {

            if (documents[0] === undefined) return  setPageStatus('forbidden');

            setSubnetDoc(documents[0]);
            setPageStatus('onload');

        }

        function onError(error) {

            console.log(error);
            setPageStatus('error-fatal');

        }

        const unsubscribe = QueryListener('subnets', [['subnetid', '==', subnetid]], onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

        // eslint-disable-next-line
    }, [getUser, getRoles]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            body={
                <div className='px-[20px]'>

                    <div className='flex justify-between'>

                        {/* ----------------------------------- */}
                        {/*  Edit Form                          */}
                        {/* ----------------------------------- */}

                        <div className='flex flex-col w-[50%] pb-[1%]'>

                            {/* Site IP */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'subnetip'}
                                label={<label className='font-medium'>Subnet</label>}
                                defaultValue={subnetDoc?.subnetip}
                                type={'text'}
                                placeholder={'10.49.128.0/20'}
                                disabled={true}
                            ></LiveField>

                            {/* Region */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'subnetip'}
                                label={<label className='font-medium'>Region</label>}
                                defaultValue={subnetDoc?.region}
                                type={'choice'}
                                options={[subnetDoc?.region]}
                                placeholder={'AUST'}
                                disabled={true}
                            ></LiveField>

                            {/* Supernet */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'supernet'}
                                label={<label className='font-medium'>Supernet</label>}
                                defaultValue={subnetDoc?.supernet}
                                type={'choice'}
                                options={[subnetDoc?.supernet]}
                                placeholder={'172.19.0.0/16'}
                                disabled={true}
                            ></LiveField>

                            {/* Site */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'siteid'}
                                label={<label className='font-medium'>Site ID</label>}
                                defaultValue={subnetDoc?.siteid}
                                type={'text'}
                                placeholder={'T3 - Level 14'}
                                disabled={false}
                                validationCallBackFunc={(value) => {

                                    if (value.length === 0) {

                                        return {
                                            'value': '',
                                            'error': 'This is a required field.'
                                        }

                                    }

                                }}
                            ></LiveField>

                            {/* Vlan ID */}
                            {
                                subnetDoc?.type !== 'region' &&
                                <LiveField
                                    collectionid={'subnets'}
                                    documentid={subnetDoc?.subnetid}
                                    fieldpath={'vlan'}
                                    label={<label className='font-medium'>Vlan ID</label>}
                                    defaultValue={subnetDoc?.vlan}
                                    type={'text'}
                                    placeholder={100}
                                    disabled={false}
                                    validationCallBackFunc={(value) => {

                                        if (value.length === 0) {

                                            return {
                                                'value': '',
                                                'error': 'This is a required field.'
                                            }

                                        }

                                    }}
                                ></LiveField>
                            }

                            {/* Description */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'description'}
                                label={<label className='font-medium'>Description</label>}
                                defaultValue={subnetDoc?.description}
                                type={'multiline'}
                                placeholder={'Add a description...'}
                                disabled={false}
                                validationCallBackFunc={(value) => {

                                    if (value === undefined) return;

                                    // Value required
                                    if(value.length === 0) return {
                                        'value': '',
                                        'error': 'This is a required field.',
                                    };

                                    return {
                                        'value': value.trim(),
                                        'error': '',
                                    };

                                }}
                            ></LiveField>

                            {/* Netmask */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'subnetmask'}
                                label={<label className='font-medium'>Netmask</label>}
                                defaultValue={subnetDoc?.subnetmask}
                                type={'text'}
                                placeholder={'255.255.252.0'}
                                disabled={true}
                            ></LiveField>

                            {/* Hosts/Net */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'assignablehosts'}
                                label={<label className='font-medium'>Hosts/Net</label>}
                                defaultValue={subnetDoc?.assignablehosts}
                                type={'number'}
                                placeholder={1022}
                                disabled={true}
                            ></LiveField>

                            {/* HostMin */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'hostmin'}
                                label={<label className='font-medium'>HostMin</label>}
                                defaultValue={subnetDoc?.hostmin}
                                type={'text'}
                                placeholder={'10.49.136.1'}
                                disabled={true}
                            ></LiveField>

                            {/* HostMax */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'hostmax'}
                                label={<label className='font-medium'>HostMax</label>}
                                defaultValue={subnetDoc?.hostmax}
                                type={'text'}
                                placeholder={'10.49.139.254'}
                                disabled={true}
                            ></LiveField>

                            {/* Additional Info (VRF, VPC, Zone …) */}
                            <LiveField
                                collectionid={'subnets'}
                                documentid={subnetDoc?.subnetid}
                                fieldpath={'additionalinfo'}
                                label={<label className='font-medium'>Additional Info (VRF, VPC, Zone …)</label>}
                                defaultValue={subnetDoc?.additionalinfo}
                                type={'multiline'}
                                placeholder={'VRF, VPC, Zone...'}
                                disabled={false}
                                validationCallBackFunc={(value) => {

                                    if (value === undefined) return;

                                    // Value required
                                    if(value.length === 0) return {
                                        'value': '',
                                        'error': 'This is a required field.',
                                    };

                                    return {
                                        'value': value.trim(),
                                        'error': '',
                                    };

                                }}
                            ></LiveField>

                            {
                                (roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin') &&

                                <>

                                    {/* Created By */}
                                    <LiveField
                                        collectionid={'subnets'}
                                        documentid={subnetDoc?.subnetid}
                                        fieldpath={'createdby'}
                                        label={<label className='font-medium'>Created By</label>}
                                        defaultValue={subnetDoc?.createdby?.email}
                                        type={'text'}
                                        disabled={true}
                                    ></LiveField>

                                    {/* Created By Date */}
                                    <LiveField
                                        collectionid={'subnets'}
                                        documentid={subnetDoc?.subnetid}
                                        fieldpath={'createddate'}
                                        label={<label className='font-medium'>Created Date</label>}
                                        defaultValue={subnetDoc?.createddate}
                                        type={'timestamp'}
                                        disabled={true}
                                    ></LiveField>

                                    {/* Last Modified By */}
                                    <LiveField
                                        collectionid={'subnets'}
                                        documentid={subnetDoc?.subnetid}
                                        fieldpath={'lastmodifiedby'}
                                        label={<label className='font-medium'>Last Modified By</label>}
                                        defaultValue={subnetDoc?.lastmodifiedby?.email}
                                        type={'text'}
                                        disabled={true}
                                    ></LiveField>

                                    {/* Last Modified Date */}
                                    <LiveField
                                        collectionid={'subnets'}
                                        documentid={subnetDoc?.subnetid}
                                        fieldpath={'lastmodifieddate'}
                                        label={<label className='font-medium'>Last Modified Date</label>}
                                        defaultValue={subnetDoc?.lastmodifieddate}
                                        type={'timestamp'}
                                        disabled={true}
                                    ></LiveField>
                                </>

                            }

                        </div>

                        {/* ----------------------------------- */}
                        {/*  Tiles                              */}
                        {/* ----------------------------------- */}

                        <div className='flex flex-col gap-3'>

                            {/* Delete Record */}
                            <div className='border-solid border-1 rounded-lg shadow-md w-[300px]'>

                                <div className={`flex flex-row justify-between items-center py-[12px] px-[20px] ${((roleStatus === 'admin' || roleStatus === 'delete') || (roleStatus === 'write' && subnetDoc?.type === 'subnet')) ? 'cursor-pointer' : 'cursor-not-allowed'}`} onClick={() => { ((roleStatus === 'admin' || roleStatus === 'delete') || (roleStatus === 'write' && subnetDoc?.type === 'subnet')) && setOpenDeletePane(subnetDoc) }}>
                                    <label className={`font-medium ${((roleStatus === 'admin' || roleStatus === 'delete') || (roleStatus === 'write' && subnetDoc?.type === 'subnet')) ? 'cursor-pointer' : 'cursor-not-allowed'}`}> Delete Record</label>
                                    {
                                        // Role check for deleting records
                                        ((roleStatus === 'admin' || roleStatus === 'delete') || (roleStatus === 'write' && subnetDoc?.type === 'subnet')) ?
                                            <img className='w-[30px]' src={DeleteIcon} alt='Delete-Icon' ></img>
                                            :
                                            <img className='w-[30px] self-start cursor-not-allowed grayscale' src={DeleteIcon} alt='Delete-Icon-Disabled'></img>

                                    }
                                </div>

                            </div>

                        </div>

                    </div>

                    <DeleteSubnetModal
                        subnetDoc={openDeletePane}
                        setSubnetDoc={setOpenDeletePane}
                    ></DeleteSubnetModal>

                </div>
            }
        ></PageComponent>
    )
}
